import React, { useState, useRef, useEffect } from "react";

// Glossary of technical abbreviations used across the site.
const GLOSSARY = {
  "RPO": "Recovery Point Objective — the maximum acceptable amount of data loss measured in time",
  "RTO": "Recovery Time Objective — the maximum acceptable duration for restoring a service after an outage",
  "OSS/BSS": "Operations Support Systems / Business Support Systems — telco platforms for network operations and customer billing",
  "SAN": "Storage Area Network — dedicated high-speed network providing block-level storage",
  "NAS": "Network Attached Storage — file-level storage available over a network",
  "HCI": "Hyper-Converged Infrastructure — integrated compute, storage and networking in a single system",
  "ANSP": "Air Navigation Service Provider — organization operating air traffic control services",
  "ICAO": "International Civil Aviation Organization — UN agency setting global aviation standards",
  "FAT/SAT": "Factory Acceptance Test / Site Acceptance Test — pre-delivery and on-site validation procedures",
  "AODB": "Airport Operational Database — central data repository for airport operations",
  "ATC": "Air Traffic Control",
  "ATM": "Air Traffic Management",
  "DR": "Disaster Recovery",
  "IaC": "Infrastructure as Code",
  "FinOps": "Financial Operations for Cloud — practice of bringing financial accountability to cloud spending",
  "MLOps": "Machine Learning Operations — practices for deploying and maintaining ML models in production",
  "LLM": "Large Language Model",
  "LLMs": "Large Language Models",
  "API": "Application Programming Interface",
  "APIs": "Application Programming Interfaces",
  "ETL": "Extract, Transform, Load — data integration process",
  "REST": "Representational State Transfer — architectural style for web APIs",
  "SDLC": "Software Development Life Cycle",
  "QoS": "Quality of Service — mechanisms for prioritizing network traffic",
  "DNS": "Domain Name System",
  "DHCP": "Dynamic Host Configuration Protocol",
  "IPAM": "IP Address Management",
  "SD-WAN": "Software-Defined Wide Area Network",
  "MPLS": "Multi-Protocol Label Switching",
  "OT": "Operational Technology",
  "ERP": "Enterprise Resource Planning",
  "CRM": "Customer Relationship Management",
  "SaaS": "Software as a Service",
  "PACS": "Picture Archiving and Communication System — medical imaging storage",
};

// Sort terms by length desc so longer tokens match first (e.g. "OSS/BSS" before "OSS")
const TERMS = Object.keys(GLOSSARY)
  .filter((k) => GLOSSARY[k])
  .sort((a, b) => b.length - a.length);

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const PATTERN = new RegExp(`(${TERMS.map(escape).join("|")})`, "g");

// Custom tooltip that reliably works on both desktop hover and mobile tap.
function Term({ term, definition }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [open]);

  const openNow = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const closeDelayed = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(false), 100);
  };
  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <span
      ref={wrapperRef}
      className="term-wrap"
      onMouseEnter={openNow}
      onMouseLeave={closeDelayed}
      data-testid={`term-${term.replace(/[^a-zA-Z0-9]/g, "-")}`}
    >
      <button
        type="button"
        className="term-trigger"
        onClick={toggle}
        aria-expanded={open}
        aria-describedby={`tt-${term}`}
      >
        {term}
      </button>
      {open && (
        <span id={`tt-${term}`} role="tooltip" className="term-tooltip">
          <span className="term-tooltip-title">{term}</span>
          <span className="term-tooltip-body">{definition}</span>
        </span>
      )}
    </span>
  );
}

/**
 * Render a plain string, wrapping any known technical abbreviation in an
 * interactive tooltip that works on hover (desktop) and tap (mobile).
 */
export function renderRichText(text) {
  if (!text || TERMS.length === 0) return text;
  const parts = text.split(PATTERN);
  return parts.map((part, i) => {
    if (i % 2 === 1 && GLOSSARY[part]) {
      return <Term key={i} term={part} definition={GLOSSARY[part]} />;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}
